import branditRails from '../../../../api/brandit_rails'
import {
  setFeaturebaseStatusAction,
  setFeaturebaseChangelogAction,
  setFeaturebseTaskAction,
  setFeaturebaseErrorsAction,
  fetchIntegrationsFromAgencyIntegrationsAction,
  fetchIntegrationAction,
  fetchAgencyIntegrationAction,
  fetchAgencyIntegrationsByIntegrationIdAction,
  fetchAvailablePortalsAction,
  fetchPortalIntegrationsAction,
  fetchPortalIntegrationAction,
  createPortalIntegrationAction,
  updatePortalIntegrationAction,
  clearAgencyIntegrationDetailAction
} from './integrations.actions'

/** @typedef {import('redux').Dispatch} */

const handleStorageAccess = async () => {
  if (!document.hasStorageAccess) throw new Error('Storage access is required.')

  const hasAccess = await document.hasStorageAccess()
  if (!hasAccess) {
    await document.requestStorageAccess()
  }
}

/**
 *
 * @param {import('../../../../reducers/adminsReducer').CurrentAdmin} currentAdmin
 * @param {import('../../../../contexts/MFAdminAppInitializer/MFAdminAppInitializer.types').MFAdminLayoutSkinType} skin
 * @returns {(dispatch: Dispatch) => void}
 */
export const initializeFeaturebaseIdentify = currentAdmin => async dispatch => {
  try {
    dispatch(setFeaturebaseStatusAction('loading'))
    dispatch(setFeaturebseTaskAction('identity'))
    await handleStorageAccess()

    if (!currentAdmin?.name) return

    if (typeof window.Featurebase !== 'function') {
      window.Featurebase = function () {
        ;(window.Featurebase.q = window.Featurebase.q || []).push(arguments)
      }
    }

    window.Featurebase('identify', {
      appId: '66a91417c971ba693ecfdeb9',
      organization: 'marketfuel',
      userId: String(currentAdmin.id),
      email: currentAdmin.email,
      name: currentAdmin.name,
      userHash: currentAdmin.featurebase_user_hash
    })

    dispatch(setFeaturebaseStatusAction('succeeded'))
  } catch (err) {
    dispatch(setFeaturebaseStatusAction('failed'))
    dispatch(setFeaturebaseErrorsAction({ message: err.message }))
  }
}

export const initializeFeaturebaseChangelog = (currentAdmin, skin) => async dispatch => {
  try {
    dispatch(setFeaturebaseStatusAction('loading'))
    dispatch(setFeaturebseTaskAction('changelog'))
    handleStorageAccess()

    if (!currentAdmin?.name) return

    if (typeof window.Featurebase !== 'function')
      window.Featurebase = function () {
        ;(window.Featurebase.q = window.Featurebase.q || []).push(arguments)
      }

    window.Featurebase(
      'initialize_changelog_widget',
      {
        organization: 'marketfuel',
        placement: 'top',
        theme: skin ?? 'light',
        fullscreenPopup: false,
        locale: 'en',
        usersName: currentAdmin.name,
        jwtToken: currentAdmin.featurebase_authentication_token
      },
      (error, data) => {
        dispatch(setFeaturebaseChangelogAction({ unreadChanges: data.unreadCount || 0 }))

        if (error) throw new Error(error)
      }
    )

    dispatch(setFeaturebaseStatusAction('succeeded'))
  } catch (err) {
    dispatch(setFeaturebaseStatusAction('failed'))
    dispatch(setFeaturebaseErrorsAction({ message: err.message }))
  }
}

export const initializeFeaturebaseMessenger = (currentAdmin, skin) => async dispatch => {
  try {
    dispatch(setFeaturebaseStatusAction('loading'))
    dispatch(setFeaturebseTaskAction('messenger'))
    await handleStorageAccess()

    if (!currentAdmin?.name) return

    if (typeof window.Featurebase !== 'function') {
      window.Featurebase = function () {
        ;(window.Featurebase.q = window.Featurebase.q || []).push(arguments)
      }
    }

    window.Featurebase('boot', {
      appId: '66a91417c971ba693ecfdeb9',
      email: currentAdmin.email,
      userId: String(currentAdmin.id),
      theme: skin ?? 'light',
      language: 'en',
      userHash: currentAdmin.featurebase_user_hash
    })

    dispatch(setFeaturebaseStatusAction('succeeded'))
  } catch (err) {
    dispatch(setFeaturebaseStatusAction('failed'))
    dispatch(setFeaturebaseErrorsAction({ message: err.message }))
  }
}

export const initializeFeaturebaseSurvey = (currentAdmin, skin) => async dispatch => {
  try {
    dispatch(setFeaturebaseStatusAction('loading'))
    dispatch(setFeaturebseTaskAction('survey'))
    await handleStorageAccess()

    if (!currentAdmin?.name) return

    if (typeof window.Featurebase !== 'function') {
      window.Featurebase = function () {
        ;(window.Featurebase.q = window.Featurebase.q || []).push(arguments)
      }
    }

    window.Featurebase('initialize_survey_widget', {
      organization: 'marketfuel',
      placement: 'bottom-right',
      theme: skin ?? 'light',
      locale: 'en',
      usersName: currentAdmin.name,
      jwtToken: currentAdmin.featurebase_authentication_token
    })

    dispatch(setFeaturebaseStatusAction('succeeded'))
  } catch (err) {
    dispatch(setFeaturebaseStatusAction('failed'))
    dispatch(setFeaturebaseErrorsAction({ message: err.message }))
  }
}

/**
 * Fetches all integrations for the IntegrationsGrid
 * @param {string|number} [integrationCategoryId] - Optional integration category ID to filter by
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const fetchIntegrations = integrationCategoryId => async dispatch => {
  try {
    const params = integrationCategoryId ? { integration_category_id: integrationCategoryId } : {}
    const response = await branditRails.get('/integrations', { params })
    dispatch(fetchIntegrationsFromAgencyIntegrationsAction(response.data))
  } catch (error) {
    console.error('Error fetching integrations:', error)
    throw error
  }
}

/**
 * Fetches a single integration by ID (for creating new agency integrations)
 * @param {string|number} integrationId - The integration ID
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const fetchIntegration = integrationId => async dispatch => {
  try {
    const response = await branditRails.get(`/integrations/${integrationId}`)
    dispatch(fetchIntegrationAction(response.data))
    return response.data
  } catch (error) {
    console.error('Error fetching integration:', error)
    throw error
  }
}

/**
 * Fetches a specific agency integration by ID
 * @param {string|number} integrationId - The integration ID
 * @param {string|number} agencyIntegrationId - The agency integration ID
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const fetchAgencyIntegration = (integrationId, agencyIntegrationId) => async dispatch => {
  try {
    const response = await branditRails.get(`/agency_integrations/${agencyIntegrationId}`)
    dispatch(fetchAgencyIntegrationAction(response.data))
  } catch (error) {
    console.error('Error fetching agency integration:', error)
    throw error
  }
}

/**
 * Fetches a specific agency integration by ID
 * @param {string|number} agencyIntegrationId - The agency integration ID
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const fetchAgencyIntegrationById =
  (agencyIntegrationId, options = {}) =>
  async dispatch => {
    try {
      const response = await branditRails.get(`/agency_integrations/${agencyIntegrationId}`, { params: options })
      dispatch(fetchAgencyIntegrationAction(response.data))
      return response.data
    } catch (error) {
      console.error('Error fetching agency integration by id:', error)
      throw error
    }
  }

/**
 * Fetches paginated portal assignment rows for a specific agency integration
 * @param {string|number} agencyIntegrationId - The agency integration ID
 * @param {Object} options - Table query parameters
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const fetchPortalAssignmentRows =
  (agencyIntegrationId, options = {}) =>
  async () => {
    const response = await branditRails.get(`/agency_integrations/${agencyIntegrationId}/portal_assignment_rows`, {
      params: options
    })
    return response.data
  }

/**
 * Fetches agency integrations by integration ID
 * @param {string|number} integrationId - The integration ID
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const fetchAgencyIntegrationsByIntegrationId = integrationId => async dispatch => {
  try {
    const response = await branditRails.get('/agency_integrations', {
      params: { integration_id: integrationId }
    })
    dispatch(fetchAgencyIntegrationsByIntegrationIdAction(response.data))
  } catch (error) {
    console.error('Error fetching agency integrations by integration ID:', error)
    throw error
  }
}

/**
 * Fetches available portals for portal configurable integrations
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const fetchAvailablePortals = () => async dispatch => {
  try {
    const response = await branditRails.get('/portal_agency_integrations/available_portals')
    dispatch(fetchAvailablePortalsAction(response.data))
  } catch (error) {
    dispatch(fetchAvailablePortalsAction({}))
    throw error
  }
}

/**
 * Fetches portal integrations for a specific portal
 * @param {string|number} portalId - The portal ID
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const fetchPortalIntegrations = portalId => async dispatch => {
  try {
    const response = await branditRails.get(`/portal/${portalId}/integrations`)
    dispatch(fetchPortalIntegrationsAction({ portalId, data: response.data }))
  } catch (error) {
    console.error('Error fetching portal integrations:', error)
    throw error
  }
}

/**
 * Fetches a specific portal integration
 * @param {string|number} portalId - The portal ID
 * @param {string|number} integrationId - The integration ID
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const fetchPortalIntegration = (portalId, integrationId) => async dispatch => {
  try {
    const response = await branditRails.get(`/portal/${portalId}/integrations/${integrationId}`)
    dispatch(fetchPortalIntegrationAction(response.data))
  } catch (error) {
    console.error('Error fetching portal integration:', error)
    throw error
  }
}

/**
 * Creates a new portal integration assignment
 * @param {string|number} portalId - The portal ID
 * @param {Object} integrationData - The integration data
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const createPortalIntegration = (portalId, integrationData) => async dispatch => {
  try {
    const response = await branditRails.post(`/portal/${portalId}/integrations`, integrationData)
    dispatch(createPortalIntegrationAction(response.data))
  } catch (error) {
    console.error('Error creating portal integration:', error)
    throw error
  }
}

/**
 * Updates a portal integration assignment
 * @param {string|number} portalId - The portal ID
 * @param {string|number} integrationId - The integration ID
 * @param {Object} integrationData - The integration data
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const updatePortalIntegration = (portalId, integrationId, integrationData) => async dispatch => {
  try {
    const response = await branditRails.patch(`/portal/${portalId}/integrations/${integrationId}`, integrationData)
    dispatch(updatePortalIntegrationAction(response.data))
  } catch (error) {
    console.error('Error updating portal integration:', error)
    throw error
  }
}

/**
 * Creates a new agency integration
 * @param {string|number} integrationId - The integration ID
 * @param {Object} integrationData - The integration data
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const createAgencyIntegration = (integrationId, integrationData) => async () => {
  const { fields, ...attributes } = integrationData
  const payload = {
    integration_id: integrationId,
    ...attributes,
    properties: { fields }
  }
  const response = await branditRails.post('/agency_integrations', payload)
  return response.data
}

/**
 * Fetches API credentials for an agency integration (when available)
 * @param {string|number} integrationId
 * @param {string|number} agencyIntegrationId
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const fetchAgencyIntegrationCredentials = (integrationId, agencyIntegrationId) => async () => {
  const response = await branditRails.get(`/agency_integrations/${agencyIntegrationId}/api_credentials`)
  return response.data
}

/**
 * Rotates API credentials for an agency integration and returns the new credentials
 * @param {string|number} integrationId
 * @param {string|number} agencyIntegrationId
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const rotateAgencyIntegrationCredentials = (integrationId, agencyIntegrationId) => async () => {
  const response = await branditRails.post(`/agency_integrations/${agencyIntegrationId}/rotate_api_credentials`)
  return response.data
}

/**
 * Updates an existing agency integration
 * @param {string|number} integrationId - The integration ID
 * @param {string|number} agencyIntegrationId - The agency integration ID
 * @param {Object} integrationData - The integration data
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const updateAgencyIntegration =
  (integrationId, agencyIntegrationId, integrationData) => async (dispatch, getState) => {
    const response = await branditRails.put(`/agency_integrations/${agencyIntegrationId}`, integrationData)

    const updatedIntegration = response.data

    // Reuse existing reducer logic to refresh the integration detail in store
    dispatch(fetchAgencyIntegrationAction(updatedIntegration))

    // Keep the cached list for this integration in sync without another request
    if (typeof getState === 'function') {
      const state = getState()
      const currentList = state?.integrations?.agencyIntegrationsByIntegrationId

      if (Array.isArray(currentList)) {
        const hasMatch = currentList.some(item => item.id === updatedIntegration.id)
        const nextList = hasMatch
          ? currentList.map(item => (item.id === updatedIntegration.id ? { ...item, ...updatedIntegration } : item))
          : [...currentList, updatedIntegration]

        dispatch(fetchAgencyIntegrationsByIntegrationIdAction(nextList))
      }
    }

    return updatedIntegration
  }

/**
 * Deletes an existing agency integration
 * @param {string|number} integrationId - The integration ID
 * @param {string|number} agencyIntegrationId - The agency integration ID
 * @returns {(dispatch: Dispatch) => Promise<void>}
 */
export const deleteAgencyIntegration = (integrationId, agencyIntegrationId) => async () => {
  await branditRails.delete(`/agency_integrations/${agencyIntegrationId}`)
}

/**
 * Updates portal agency integration assignments
 * @param {string|number} agencyIntegrationId - The agency integration ID
 * @param {Array} selectedPortals - Array of selected portal objects
 * @returns {(dispatch: Dispatch) => Promise<Object>}
 */
export const updatePortalAgencyIntegrations = (agencyIntegrationId, selectedPortals) => async dispatch => {
  const portalIds = Array.isArray(selectedPortals) ? selectedPortals.map(portal => portal.id) : []

  // send full selection and let the server reconcile
  const { data } = await branditRails.post(`/agency_integrations/${agencyIntegrationId}/portal_agency_integrations`, {
    portal_ids: portalIds
  })

  // Re-fetch to update assignedPortals state
  await dispatch(fetchAgencyIntegrationById(agencyIntegrationId))

  return data
}

/**
 * Clears the agency integration detail state
 * @returns {(dispatch: Dispatch) => void}
 */
export const clearAgencyIntegrationDetail = () => dispatch => {
  dispatch(clearAgencyIntegrationDetailAction())
}
